import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { roleService } from "@/services/roleService";
import { RoleRequest } from "@/types/api";
import { toast } from "sonner";
import { AxiosError } from "axios";

export const useRoles = () => {
  const queryClient = useQueryClient();

  // Query para listar roles
  const rolesQuery = useQuery({
    queryKey: ["roles"],
    queryFn: () => roleService.getRoles(),
  });

  // Mutation para criar role
  const createRoleMutation = useMutation({
    mutationFn: (roleData: RoleRequest) => roleService.createRole(roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast.success("Role criada com sucesso!");
    },
    onError: (error: unknown) => {
      const message =
        (error as AxiosError<{ message: string }>)?.response?.data?.message ||
        "Erro ao criar role";
      toast.error(message);
    },
  });

  // Mutation para atualizar role
  const updateRoleMutation = useMutation({
    mutationFn: ({ id, roleData }: { id: string; roleData: RoleRequest }) =>
      roleService.updateRole(id, roleData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast.success("Role atualizada com sucesso!");
    },
    onError: (error: unknown) => {
      const message =
        (error as AxiosError<{ message: string }>)?.response?.data?.message ||
        "Erro ao atualizar role";
      toast.error(message);
    },
  });

  // Mutation para deletar role
  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => roleService.deleteRole(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      toast.success("Role deletada com sucesso!");
    },
    onError: (error: unknown) => {
      const message =
        (error as AxiosError<{ message: string }>)?.response?.data?.message ||
        "Erro ao deletar role";
      toast.error(message);
    },
  });

  return {
    roles: rolesQuery.data || [],
    isLoading: rolesQuery.isLoading,
    error: rolesQuery.error,
    createRole: createRoleMutation.mutate,
    updateRole: updateRoleMutation.mutate,
    deleteRole: deleteRoleMutation.mutate,
    isCreating: createRoleMutation.isPending,
    isUpdating: updateRoleMutation.isPending,
    isDeleting: deleteRoleMutation.isPending,
  };
};

// Hook para buscar uma role específica
export const useRole = (id: string) => {
  return useQuery({
    queryKey: ["roles", id],
    queryFn: () => roleService.getRoleById(id),
    enabled: !!id,
  });
};
